import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { useAdminApi } from '@app/api/api-client-context'
import { useAuth } from '@app/auth/auth-context'
import {
	builderStateToPatch,
	normalizeProgramBuilder,
	programBuilderFingerprint,
	programKeys,
	programLifecycleLabel,
	programToBuilderState,
	type Program,
	type ProgramBuilderState,
	type ProgramValidationIssue,
	type ProgramValidationReport,
} from '@entities/program'
import { AdminApiError, createIdempotencyKey, unwrapApiResponse } from '@shared/api'
import { getInterfaceErrorDescription, getInterfaceErrorPresentation, getInterfaceErrorToast } from '@shared/i18n/interface-messages'
import { useToast } from '@shared/ui/toast'
import { readProgramDraft, removeProgramDraft, writeProgramDraft } from '../model/program-draft'

export type ProgramLifecycleAction = 'publish' | 'unpublish' | 'archive' | 'restore'
export type ProgramPendingAction = ProgramLifecycleAction | 'save' | 'validate' | 'clone' | null

export const programLifecycleMeta: Record<ProgramLifecycleAction, { title: string; description: string; label: string }> = {
	publish: { title: 'Опубликовать программу?', description: 'Сохранённая структура станет доступна пользователям после серверной проверки.', label: 'Опубликовать' },
	unpublish: { title: 'Снять программу с публикации?', description: 'Программа вернётся в черновик и снова станет доступна для редактирования.', label: 'Снять с публикации' },
	archive: { title: 'Архивировать программу?', description: 'Программа исчезнет из активного каталога, но сохранит структуру и историю версий.', label: 'Архивировать' },
	restore: { title: 'Восстановить программу?', description: 'Программа вернётся из архива в состояние черновика.', label: 'Восстановить' },
}

const recordValue = (value: unknown): Record<string, unknown> | null =>
	typeof value === 'object' && value !== null ? value as Record<string, unknown> : null

const validationIssuesFromError = (error: unknown): ProgramValidationIssue[] => {
	if (!(error instanceof AdminApiError)) return []
	const details = recordValue(error.details)
	const candidates = details?.issues ?? recordValue(details?.validation)?.issues
	if (!Array.isArray(candidates)) return []
	return candidates.flatMap(candidate => {
		const value = recordValue(candidate)
		if (!value || typeof value.message !== 'string') return []
		return [{
			code: typeof value.code === 'string' ? value.code : error.code,
			message: value.message,
			severity: value.severity === 'WARNING' ? 'WARNING' : 'ERROR',
			path: typeof value.path === 'string' ? value.path : undefined,
		} satisfies ProgramValidationIssue]
	})
}


export const useProgramWorkflow = (program: Program) => {
	const api = useAdminApi()
	const { can } = useAuth()
	const history = useHistory()
	const queryClient = useQueryClient()
	const { notify } = useToast()
	const initial = useRef<{ program: Program; draft: ProgramBuilderState; baseline: ProgramBuilderState; etag: string; restored: boolean } | null>(null)
	if (!initial.current) {
		const backup = readProgramDraft(program.id)
		const server = programToBuilderState(program)
		initial.current = backup
			? { program, draft: backup.draft, baseline: backup.baseline, etag: backup.etag, restored: true }
			: { program, draft: server, baseline: server, etag: program.etag, restored: false }
	}
	const activeProgramId = useRef(program.id)
	const attemptKeys = useRef<Record<ProgramLifecycleAction | 'clone', string>>({
		publish: createIdempotencyKey(),
		unpublish: createIdempotencyKey(),
		archive: createIdempotencyKey(),
		restore: createIdempotencyKey(),
		clone: createIdempotencyKey(),
	})
	const [currentProgram, setCurrentProgram] = useState(initial.current.program)
	const [draft, setDraft] = useState(initial.current.draft)
	const [baselineState, setBaselineState] = useState(initial.current.baseline)
	const [etag, setEtag] = useState(initial.current.etag)
	const [restoredDraft, setRestoredDraft] = useState(initial.current.restored)
	const [serverValidation, setServerValidation] = useState<ProgramValidationReport | null>(null)
	const [conflict, setConflict] = useState<{ version: number; etag: string } | null>(null)
	const [pendingAction, setPendingAction] = useState<ProgramPendingAction>(null)

	useEffect(() => {
		if (activeProgramId.current === program.id) return
		activeProgramId.current = program.id
		const backup = readProgramDraft(program.id)
		const server = programToBuilderState(program)
		setCurrentProgram(program)
		setDraft(backup?.draft ?? server)
		setBaselineState(backup?.baseline ?? server)
		setEtag(backup?.etag ?? program.etag)
		setRestoredDraft(Boolean(backup))
		setServerValidation(null)
		setConflict(null)
	}, [program])

	const normalized = useMemo(() => normalizeProgramBuilder(draft), [draft])
	const fingerprint = useMemo(() => programBuilderFingerprint(normalized), [normalized])
	const baseline = useMemo(() => programBuilderFingerprint(baselineState), [baselineState])
	const dirty = fingerprint !== baseline
	const editable = can('updateProgram') && currentProgram.lifecycle === 'DRAFT'

	useEffect(() => {
		setServerValidation(null)
	}, [fingerprint])

	useEffect(() => {
		if (dirty) {
			writeProgramDraft({ program_id: currentProgram.id, draft: normalized, baseline: baselineState, etag, saved_at: new Date().toISOString() })
		} else {
			removeProgramDraft(currentProgram.id)
		}
	}, [baselineState, currentProgram.id, dirty, etag, normalized])

	useEffect(() => {
		if (!dirty) return
		const listener = (event: BeforeUnloadEvent) => {
			event.preventDefault()
			event.returnValue = ''
		}
		window.addEventListener('beforeunload', listener)
		const unblock = history.block('Есть несохранённые изменения программы. Покинуть страницу?')
		return () => {
			window.removeEventListener('beforeunload', listener)
			unblock()
		}
	}, [dirty, history])

	const fetchLatest = async () => unwrapApiResponse(await api.GET('/admin/v1/programs/{program_id}', {
		params: { path: { program_id: currentProgram.id } },
	}))

	const adoptServerProgram = (saved: Program, responseEtag?: string | null) => {
		const next = programToBuilderState(saved)
		queryClient.setQueryData(programKeys.detail(saved.id), saved)
		setCurrentProgram(saved)
		setDraft(next)
		setBaselineState(next)
		setEtag(responseEtag ?? saved.etag)
		setRestoredDraft(false)
		setConflict(null)
		setServerValidation(null)
		removeProgramDraft(saved.id)
	}

	const preserveDraftAfterConflict = async () => {
		const latest = await fetchLatest()
		queryClient.setQueryData(programKeys.detail(currentProgram.id), latest.data)
		setCurrentProgram(latest.data)
		setEtag(latest.etag ?? latest.data.etag)
		setConflict({ version: latest.data.version, etag: latest.etag ?? latest.data.etag })
	}


	const save = useMutation({
		mutationFn: async () => {
			setPendingAction('save')
			return unwrapApiResponse(await api.PATCH('/admin/v1/programs/{program_id}', {
				params: { path: { program_id: currentProgram.id }, header: { 'If-Match': etag } },
				body: builderStateToPatch(normalized, baselineState),
			}))
		},
		onSuccess: async result => {
			adoptServerProgram(result.data, result.etag)
			await queryClient.invalidateQueries({ queryKey: programKeys.all })
			notify({ title: 'Программа сохранена', description: `Версия ${result.data.version}`, tone: 'success' })
		},
		onError: async error => {
			const validationIssues = validationIssuesFromError(error)
			if (validationIssues.length) setServerValidation({ valid: false, issues: validationIssues })
			if (error instanceof AdminApiError && (error.status === 409 || error.status === 412)) {
				try {
					await preserveDraftAfterConflict()
					const presentation = getInterfaceErrorPresentation(error)
					notify({
						...getInterfaceErrorToast(error),
						description: `Локальный черновик сохранён. ${presentation.action ?? 'Обновите данные и повторите операцию.'}`,
					})
					return
				} catch {
					return
				}
			}
			notify({ title: 'Не удалось сохранить программу', description: getInterfaceErrorDescription(error), tone: 'error' })
		},
		onSettled: () => setPendingAction(null),
	})

	const validate = useMutation({
		mutationFn: async () => {
			setPendingAction('validate')
			return unwrapApiResponse(await api.POST('/admin/v1/programs/{program_id}/validate', {
				params: { path: { program_id: currentProgram.id } },
			})).data
		},
		onSuccess: report => {
			setServerValidation(report)
			notify({ title: report.valid ? 'Проверка пройдена' : 'Найдены ошибки', description: report.valid ? 'Сохранённая структура готова к проверке статуса.' : `${report.issues.length} проблем требуют исправления.`, tone: report.valid ? 'success' : 'error' })
		},
		onError: error => notify({ title: 'Не удалось проверить программу', description: getInterfaceErrorDescription(error), tone: 'error' }),
		onSettled: () => setPendingAction(null),
	})

	const lifecycleRequest = async (action: ProgramLifecycleAction) => {
		const header = { 'Idempotency-Key': attemptKeys.current[action], 'If-Match': etag }
		switch (action) {
			case 'publish':
				return unwrapApiResponse(await api.POST('/admin/v1/programs/{program_id}/publish', { params: { path: { program_id: currentProgram.id }, header } }))
			case 'unpublish':
				return unwrapApiResponse(await api.POST('/admin/v1/programs/{program_id}/unpublish', { params: { path: { program_id: currentProgram.id }, header } }))
			case 'archive':
				return unwrapApiResponse(await api.POST('/admin/v1/programs/{program_id}/archive', { params: { path: { program_id: currentProgram.id }, header } }))
			case 'restore':
				return unwrapApiResponse(await api.POST('/admin/v1/programs/{program_id}/restore', { params: { path: { program_id: currentProgram.id }, header } }))
		}
	}

	const lifecycle = useMutation({
		mutationFn: async (action: ProgramLifecycleAction) => {
			setPendingAction(action)
			await lifecycleRequest(action)
			const latest = await fetchLatest()
			return { action, latest }
		},
		onSuccess: async ({ action, latest }) => {
			attemptKeys.current[action] = createIdempotencyKey()
			adoptServerProgram(latest.data, latest.etag)
			await queryClient.invalidateQueries({ queryKey: programKeys.all })
			notify({ title: programLifecycleMeta[action].label, description: `Текущий статус: ${programLifecycleLabel[latest.data.lifecycle]}.`, tone: 'success' })
		},
		onError: async error => {
			const validationIssues = validationIssuesFromError(error)
			if (validationIssues.length) setServerValidation({ valid: false, issues: validationIssues })
			if (error instanceof AdminApiError && (error.status === 409 || error.status === 412)) {
				try {
					const latest = await fetchLatest()
					adoptServerProgram(latest.data, latest.etag)
				} catch {
					return
				}
			}
			notify({ title: 'Не удалось изменить статус программы', description: getInterfaceErrorDescription(error), tone: 'error' })
		},
		onSettled: () => setPendingAction(null),
	})


	const clone = useMutation({
		mutationFn: async (name: string) => {
			setPendingAction('clone')
			return unwrapApiResponse(await api.POST('/admin/v1/programs/{program_id}/clone', {
				params: { path: { program_id: currentProgram.id }, header: { 'Idempotency-Key': attemptKeys.current.clone } },
				body: { name: name.trim() },
			})).data
		},
		onSuccess: async cloned => {
			attemptKeys.current.clone = createIdempotencyKey()
			await queryClient.invalidateQueries({ queryKey: programKeys.all })
			notify({ title: 'Копия создана', description: cloned.name, tone: 'success' })
			history.push(`/programs/${cloned.id}`)
		},
		onError: error => notify({ title: 'Не удалось клонировать программу', description: getInterfaceErrorDescription(error), tone: 'error' }),
		onSettled: () => setPendingAction(null),
	})

	const discardLocalDraft = () => {
		const next = programToBuilderState(currentProgram)
		setDraft(next)
		setBaselineState(next)
		setEtag(currentProgram.etag)
		setRestoredDraft(false)
		setConflict(null)
		setServerValidation(null)
		removeProgramDraft(currentProgram.id)
	}

	return {
		currentProgram,
		draft,
		setDraft,
		baselineState,
		etag,
		normalized,
		dirty,
		editable,
		restoredDraft,
		serverValidation,
		conflict,
		pendingAction,
		save,
		validate,
		lifecycle,
		clone,
		discardLocalDraft,
		resetCloneAttempt: () => { attemptKeys.current.clone = createIdempotencyKey() },
	}
}
